import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Listbox } from '@headlessui/react';
import moment from 'moment';
import Compressor from 'compressorjs';
import {
	PaperClipIcon,
	XMarkIcon,
	ClipboardDocumentListIcon,
	CalendarDaysIcon,
	UserIcon,
	CheckIcon,
} from '@heroicons/react/20/solid';
import {
	PencilIcon,
	ClockIcon,
	CalendarIcon,
	PlusIcon,
	ArrowUpTrayIcon,
	FolderOpenIcon,
	ChartBarIcon,
	ExclamationTriangleIcon,
	DocumentTextIcon,
} from '@heroicons/react/24/outline';

import {
	getWorkOrder,
	updateWorkOrder,
	updateWorkOrderStatus,
	addWorkOrderComment,
	addWorkOrderDocuments,
	removeWorkOrderDocument,
	getWorkOrderChecklist,
	createWorkOrderChecklistItem,
	updateWorkOrderChecklistItem,
	reorderWorkOrderChecklistItems,
	deleteWorkOrderChecklistItem,
	updateTicket,
} from '../api';
import { getTeam } from '../api/company';
import { getModules, getModuleCategories } from '../api/modules';
import { uploadDocumentBeta } from '../api/documents';

import {
	StackableModal,
	Tabs,
	AssetList,
	TaskList,
	StatusBadge,
	Badge,
	DatePicker,
	DateTimePicker,
	AttachmentList,
	ImageGallery,
	getNonImageAttachments,
	TextStack,
	AssetInfoCard,
	FileUpload,
} from '../components/nextgen';
import { classNames, getUserFullName } from '../components/helpers/functions';
import Button from '../components/Button';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';
import { AvatarGenerated } from '../components/Avatar';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import useLocal from '../hooks/useLocal';
import useSettings from '../hooks/useSettings';
import Banner from '../components/Banner';

const MODAL_NAME = 'workOrderOverview';

// Status configuration
const STATUS_OPTIONS = [
	{ key: 'draft', name: 'Draft', color: 'gray' },
	{ key: 'pending', name: 'Pending', color: 'yellow' },
	{ key: 'scheduled', name: 'Scheduled', color: 'blue' },
	{ key: 'in_progress', name: 'In Progress', color: 'indigo' },
	{ key: 'on_hold', name: 'On Hold', color: 'orange' },
	{ key: 'completed', name: 'Completed', color: 'green' },
	{ key: 'cancelled', name: 'Cancelled', color: 'red' },
];

const PRIORITY_OPTIONS = [
	{ key: 'low', name: 'Low', color: 'gray' },
	{ key: 'medium', name: 'Medium', color: 'yellow' },
	{ key: 'high', name: 'High', color: 'orange' },
	{ key: 'critical', name: 'Critical', color: 'red' },
];

// Due date status helper
const getDueDateStatus = (dueDate, status) => {
	if (!dueDate || ['completed', 'cancelled', 'on_hold'].includes(status)) {
		return { status: 'normal', className: 'text-gray-900' };
	}

	const now = moment().startOf('day');
	const due = moment(dueDate).startOf('day');
	const daysUntilDue = due.diff(now, 'days');

	if (daysUntilDue < 0) {
		return { status: 'overdue', className: 'text-red-600 font-medium' };
	}
	return { status: 'normal', className: 'text-gray-900' };
};

const Editable = ({ children, disabled, onClick }) => (
	<div
		className={classNames(
			'group flex items-center',
			disabled ? 'cursor-default' : 'cursor-pointer',
		)}
		onClick={() => typeof onClick === 'function' && !disabled && onClick()}
	>
		{children}
		{!disabled && (
			<div className="ml-2 opacity-0 group-hover:opacity-100 transition duration-200">
				<PencilIcon className="w-3 h-3 text-gray-400" />
			</div>
		)}
	</div>
);

const generateActivityLine = (activity) => {
	const name = activity?.addedBy
		? `${activity.addedBy.firstName} ${activity.addedBy.lastName}`
		: 'System';

	const data =
		activity?.dataType === 'object' && activity?.data
			? JSON.parse(activity.data)
			: activity?.data;

	switch (activity?.type) {
		case 'work_order.created':
			return (
				<>
					<span className="font-medium text-gray-900">{name}</span> created this
					work order
				</>
			);
		case 'status.updated':
			return (
				<>
					<span className="font-medium text-gray-900">{name}</span> updated the
					status to <StatusBadge status={data?.to || data} />
				</>
			);
		case 'comment.created':
			return (
				<>
					<span className="font-medium text-gray-900">{name}</span> commented:
					<div className="mt-1 text-gray-600 bg-gray-50 rounded p-2 text-sm">
						{data}
					</div>
				</>
			);
		case 'assignedTo.updated':
			return (
				<>
					<span className="font-medium text-gray-900">{name}</span> updated the
					assignment
				</>
			);
		default:
			return (
				<>
					<span className="font-medium text-gray-900">{name}</span> made an
					update
				</>
			);
	}
};

const WorkOrderOverview = () => {
	const navigate = useNavigate();

	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: onOpenDeleteModal, setClose: closeDeleteModal } =
		useModal('deleteModal');
	const { setOpen: openEditWorkOrder } = useModal('editWorkOrder');
	const { setOpen: openDocumentPicker } = useModal('documentPicker');
	const { setOpen: openDocumentPreview } = useModal('documentPreview');
	const { setOpen: openTicketOverview } = useModal('ticketOverview');
	const { setOpen: openAddCategory } = useModal('addCategoryModal');
	const { setOpen: openStatistics } = useModal('statisticsModal');
	const { setOpen: openContract } = useModal('contractOverview');
	const { setOpen: openViewContact } = useModal('viewContact');
	const { setOpen: openTaskDetail } = useModal('taskDetail');
	const { setOpen: openSopViewer } = useModal('sopViewer');

	const fileInputRef = useRef(null);
	const [form, setForm] = useState({});
	const [isEditingField, setIsEditingField] = useState(null);
	const [comment, setComment] = useState('');
	const [isUploading, setIsUploading] = useState(false);
	const [activeTab, setActiveTab] = useState('tasks');
	const [showUploadOptions, setShowUploadOptions] = useState(false);
	const [isUploadingServiceReport, setIsUploadingServiceReport] =
		useState(false);
	const [showTicketResolveModal, setShowTicketResolveModal] = useState(false);
	const [assetsCollapsed, setAssetsCollapsed] = useState(true);
	const [isAllDayEvent, setIsAllDayEvent] = useState(true);
	const [serviceTimes, setServiceTimes] = useState({
		startHour: '09',
		startMinute: '00',
		endHour: '17',
		endMinute: '00',
	});

	const { get } = useLocal();
	const user = get('user');
	const settings = useSettings();
	const assetDisplayTemplate = settings.get('assetDisplay');

	/**
	 * Parse a handlebars-like template and replace variables with asset values.
	 * Supports: serialNumber, location, tag, alias, and custom attribute keys.
	 */
	const parseAssetTemplate = (template, asset) => {
		if (!template) return null;

		const location = asset.location?.name || asset.locationName || '';

		const values = {
			serialNumber: asset.serialNumber || '',
			location: location,
			tag: asset.tag || '',
			alias: asset.alias || '',
		};

		if (asset.customAttributes) {
			Object.entries(asset.customAttributes).forEach(([key, value]) => {
				if (typeof value === 'string' || typeof value === 'number') {
					values[key] = value;
				}
			});
		}

		Object.entries(asset).forEach(([key, value]) => {
			if (
				!values[key] &&
				(typeof value === 'string' || typeof value === 'number')
			) {
				values[key] = value;
			}
		});

		return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, variable) => {
			const value = values[variable];
			return value !== undefined && value !== null && value !== ''
				? String(value)
				: '';
		});
	};

	const handleClose = (path) => {
		setClose();
		setActiveTab('tasks');
		if (typeof open?.onClose === 'function') {
			open?.onClose(path);
		}
	};

	const handleEdit = () => {
		openEditWorkOrder({
			workOrder,
			onSuccess: () => {
				refreshWorkOrder({ id: open?.id });
				refreshChecklist({ id: open?.id });
				if (typeof open?.onSuccess === 'function') {
					open.onSuccess();
				}
			},
			onDeleted: () => {
				if (typeof open?.onSuccess === 'function') {
					open.onSuccess();
				}
				handleClose();
			},
		});
	};

	// API hooks
	const {
		value: workOrder,
		status,
		execute: refreshWorkOrder,
	} = useAsync(getWorkOrder, { immediate: false });

	const { execute: onUpdate, status: updating } = useAsync(updateWorkOrder, {
		immediate: false,
	});

	const { execute: onUpdateStatus } = useAsync(updateWorkOrderStatus, {
		immediate: false,
	});

	const { execute: onComment, status: commentStatus } = useAsync(
		addWorkOrderComment,
		{ immediate: false },
	);

	const { value: team } = useAsync(getTeam);

	const { value: modules, execute: onRefreshModules } = useAsync(getModules, {
		immediate: false,
	});

	const workOrdersModule = modules?.rows?.find(
		(m) => m.key === 'default:work-order',
	);

	const { value: categories, execute: onRefreshCategories } = useAsync(
		getModuleCategories,
		{ immediate: false },
	);

	const { execute: onUploadDocuments } = useAsync(addWorkOrderDocuments, {
		immediate: false,
	});

	const { execute: onRemoveDocument } = useAsync(removeWorkOrderDocument, {
		immediate: false,
	});

	// Checklist hooks
	const { value: checklistItems, execute: refreshChecklist } = useAsync(
		getWorkOrderChecklist,
		{ immediate: false },
	);

	const { execute: onCreateChecklistItem } = useAsync(
		createWorkOrderChecklistItem,
		{ immediate: false },
	);

	const { execute: onUpdateChecklistItem } = useAsync(
		updateWorkOrderChecklistItem,
		{ immediate: false },
	);

	const { execute: onDeleteChecklistItem } = useAsync(
		deleteWorkOrderChecklistItem,
		{ immediate: false },
	);

	const { execute: onReorderChecklistItems } = useAsync(
		reorderWorkOrderChecklistItems,
		{ immediate: false },
	);

	// Load work order data
	useEffect(() => {
		if (open?.id) {
			refreshWorkOrder({ id: open.id });
			refreshChecklist({ id: open.id });
		}
	}, [open?.id]);

	// Initialize form when work order loads
	useEffect(() => {
		if (workOrder) {
			setForm({
				title: workOrder.title,
				description: workOrder.description,
				status: workOrder.status,
				priority: workOrder.priority,
				assignedTo: workOrder.assignedTo?.id || 'unassigned',
				dueDate: workOrder.dueDate,
				estimatedHours: workOrder.estimatedHours,
				actualHours: workOrder.actualHours,
			});
		}
	}, [workOrder]);

	// Fetch modules when modal opens
	useEffect(() => {
		if (open?.id) {
			onRefreshModules();
		}
	}, [open?.id]);

	// Fetch categories when workOrdersModule is available
	useEffect(() => {
		if (workOrdersModule?.id) {
			onRefreshCategories({ id: workOrdersModule.id });
		}
	}, [workOrdersModule?.id]);

	// File upload or link document
	const uploadFile = (file) => {
		setIsUploading(true);

		// Check if this is a document from library (has ID and fromLibrary flag)
		if (file.fromLibrary && file.id) {
			// Link existing document by ID
			onUploadDocuments({ id: open?.id, documentIds: [file.id] }).then(() => {
				setIsUploading(false);
				refreshWorkOrder({ id: open?.id });
			});
		} else {
			// Upload new file
			onUploadDocuments({ id: open?.id, files: [file] }).then(() => {
				setIsUploading(false);
				refreshWorkOrder({ id: open?.id });
			});
		}
	};

	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		if (file.type.startsWith('image/')) {
			setIsUploading(true);
			new Compressor(file, {
				quality: 0.6,
				maxWidth: 1920,
				success: (compressedBlob) => {
					const compressedFile = new File([compressedBlob], file.name, {
						type: compressedBlob.type,
					});
					uploadFile(compressedFile);
				},
				error: () => uploadFile(file),
			});
		} else {
			uploadFile(file);
		}
		e.target.value = '';
	};

	// Handle service report file selection (from FileUpload component)
	const handleServiceReportChange = async (files) => {
		if (!files || files.length === 0) return;

		const file = files[0];
		setIsUploadingServiceReport(true);

		try {
			if (file.fromLibrary) {
				// Selected from document library - just set the serviceReportId
				await onUpdate({ id: open?.id, serviceReportId: file.id });
			} else {
				// Direct file upload - upload to document library first
				const result = await uploadDocumentBeta({
					files: [file.original],
					bucket: 'work-orders',
				});

				if (result?.id) {
					await onUpdate({ id: open?.id, serviceReportId: result.id });
				}
			}

			refreshWorkOrder({ id: open?.id });
		} catch (error) {
			console.error('Failed to set service report:', error);
		} finally {
			setIsUploadingServiceReport(false);
		}
	};

	// Update field
	const handleUpdateField = async (field, value) => {
		await onUpdate({ id: open?.id, [field]: value });
		refreshWorkOrder({ id: open?.id });
		setIsEditingField(null);
	};

	// Helper function to complete work order with ticket check
	const completeWorkOrder = async () => {
		// Check if there's an associated ticket
		if (
			workOrder?.ticket?.id &&
			workOrder?.ticket?.status !== 'done' &&
			workOrder?.ticket?.status !== 'closed'
		) {
			// Show modal to ask user if they want to resolve the ticket too
			setShowTicketResolveModal(true);
		} else {
			// No ticket or ticket already resolved, just complete the work order
			await onUpdateStatus({ id: open?.id, status: 'completed' });
			refreshWorkOrder({ id: open?.id });
			// Open statistics modal after completion
			openStatistics({ entityType: 'workorder', id: open?.id });
		}
	};

	// Handle completing work order and optionally resolving ticket
	const handleCompleteWithTicket = async (resolveTicket) => {
		setShowTicketResolveModal(false);

		try {
			// Complete work order
			await onUpdateStatus({ id: open?.id, status: 'completed' });

			// Optionally resolve ticket
			if (resolveTicket && workOrder?.ticket?.id) {
				await updateTicket({ id: workOrder.ticket.id, status: 'done' });
			}

			await refreshWorkOrder({ id: open?.id });
			// Open statistics modal after completion
			openStatistics({ entityType: 'workorder', id: open?.id });
		} catch (error) {
			console.error('Failed to complete work order:', error);
		}
	};

	// Update status
	const handleStatusChange = async (newStatus) => {
		// If completing, check for ticket
		if (newStatus === 'completed') {
			await completeWorkOrder();
		} else {
			// For other status changes, just update normally
			await onUpdateStatus({ id: open?.id, status: newStatus });
			refreshWorkOrder({ id: open?.id });
		}
	};

	// Submit comment
	const handleSubmitComment = async () => {
		if (!comment.trim()) return;
		await onComment({ id: open?.id, comment: comment.trim() });
		setComment('');
		refreshWorkOrder({ id: open?.id });
	};

	// Checklist handlers
	const handleToggleTask = async (taskId, completed, userInput) => {
		await onUpdateChecklistItem({
			id: open?.id,
			checklistItemId: taskId,
			completed: !completed,
			userInput,
		});
		refreshChecklist({ id: open?.id });
	};

	const handleInputChange = async (taskId, userInput) => {
		await onUpdateChecklistItem({
			id: open?.id,
			checklistItemId: taskId,
			userInput,
		});
		// Don't refresh immediately to avoid losing focus - let it refresh on next action
	};

	const handleDeleteTask = async (taskId) => {
		await onDeleteChecklistItem({ id: open?.id, checklistItemId: taskId });
		refreshChecklist({ id: open?.id });
	};

	const handleReorderTasks = async (items) => {
		await onReorderChecklistItems({ id: open?.id, items });
		refreshChecklist({ id: open?.id });
	};

	const handleAssignTask = async (taskId, userId) => {
		await onUpdateChecklistItem({
			id: open?.id,
			checklistItemId: taskId,
			assignedTo: userId,
		});
		refreshChecklist({ id: open?.id });
	};

	const handleDueDateChange = async (taskId, dueDate) => {
		await onUpdateChecklistItem({
			id: open?.id,
			checklistItemId: taskId,
			dueDate: dueDate,
		});
		refreshChecklist({ id: open?.id });
	};

	const isLoading = status === 'pending';
	const isCompleted =
		workOrder?.status === 'completed' || workOrder?.status === 'cancelled';

	console.log('workOrder: ', workOrder);
	return (
		<>
			<StackableModal
				open={!!open}
				onClose={handleClose}
				size="7xl"
				dialogClassName="min-h-[600px]"
			>
				{isLoading ? (
					<LoadingSkeleton />
				) : (
					<div className="grid grid-cols-1 lg:grid-cols-12 gap-8 text-left -mt-4 lg:mt-0">
						{/* Mobile close button */}
						<div className="sm:hidden flex justify-end -mr-2 -mb-4 col-span-full">
							<button
								type="button"
								className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
								onClick={() => handleClose()}
							>
								<span className="sr-only">Close</span>
								<XMarkIcon className="h-5 w-5" />
							</button>
						</div>

						{/* Column 1 - Work Order Information */}
						<div className="lg:col-span-8">
							{/* Header with code and status */}

							{workOrder?.serviceReportRequired &&
								!workOrder?.serviceReport &&
								isCompleted && (
									<Banner
										status="warning"
										className={'mb-6 rounded-tl-lg -ml-10 -mt-6'}
									>
										<div className="flex items-center">
											<ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mr-3" />
											Missing service report
										</div>
									</Banner>
								)}

							<div className="space-x-2">
								<StatusBadge rounded="sm">{`Work Order: #${workOrder?.workOrderCode}`}</StatusBadge>
								<StatusBadge status={workOrder?.status} />
							</div>

							{/* Title */}
							<div className="text-lg mb-1 pb-4 mt-4">
								<div className="font-semibold">
									{workOrder?.title || 'Untitled Work Order'}
								</div>

								{/* Service Period & Due Date - Inline */}
								<div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
									{/* Service Period */}
									{(workOrder?.scheduledStartDate ||
										workOrder?.scheduledEndDate) && (
										<div className="text-sm text-gray-600 flex items-center gap-1.5">
											<CalendarIcon className="h-4 w-4 text-gray-400" />
											{(() => {
												const start = workOrder?.scheduledStartDate;
												const end = workOrder?.scheduledEndDate;
												const isAllDay = workOrder?.allDay;

												if (
													start &&
													end &&
													moment(start).isSame(moment(end), 'day')
												) {
													// Same day
													if (isAllDay) {
														return moment(start).format('ddd, MMM D, YYYY');
													}
													// Same day with times
													return `${moment(start).format('ddd, MMM D, YYYY')} ${moment(start).format('h:mm A')} - ${moment(end).format('h:mm A')}`;
												} else if (start && end) {
													// Date range
													if (isAllDay) {
														return `${moment(start).format('ddd, MMM D')} - ${moment(end).format('ddd, MMM D, YYYY')}`;
													}
													// Date range with times
													return `${moment(start).format('ddd, MMM D, h:mm A')} - ${moment(end).format('ddd, MMM D, YYYY h:mm A')}`;
												} else if (start) {
													if (isAllDay) {
														return moment(start).format('ddd, MMM D, YYYY');
													}
													return `${moment(start).format('ddd, MMM D, YYYY h:mm A')}`;
												} else if (end) {
													if (isAllDay) {
														return moment(end).format('ddd, MMM D, YYYY');
													}
													return `${moment(end).format('ddd, MMM D, YYYY h:mm A')}`;
												}
												return '';
											})()}
										</div>
									)}

									{/* Due Date */}
									{/*workOrder?.status === 'completed' && workOrder?.completedDate ? (
										<StatusBadge value="completed">
											Completed on{' '}
											{moment(workOrder.completedDate).format('ddd, MMM D, YYYY')}
										</StatusBadge>
									) : (
										<div
											className={`text-sm flex items-center gap-1.5 ${getDueDateStatus(workOrder?.dueDate, workOrder?.status).className}`}
										>
											<ClockIcon className="h-4 w-4 text-gray-400" />
											Due:{' '}
											{workOrder?.dueDate
												? moment(workOrder.dueDate).format('ddd, MMM D, YYYY')
												: 'Not set'}
										</div>
									)*/}
								</div>
							</div>

							{/* Mobile: Key attributes below description */}
							<div className="lg:hidden grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-b pb-6 border-gray-200">
								{/* Service Period - full width */}
								<div className="text-left col-span-2">
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider mb-1">
										Service Period
									</div>
									<DateTimePicker
										mode="range"
										startDate={workOrder?.scheduledStartDate}
										endDate={workOrder?.scheduledEndDate}
										onChange={(startDate, endDate) => {
											if (isCompleted) return;
											onUpdate({
												id: open?.id,
												scheduledStartDate: startDate,
												scheduledEndDate: endDate,
											}).then(() => refreshWorkOrder({ id: open?.id }));
										}}
										placeholder="Set service period"
										showTimeToggle={true}
										defaultAllDay={true}
										disabled={isCompleted}
									/>
								</div>
								<div className="text-left">
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider mb-1">
										Due Date
									</div>
									<div className="mt-1">
										<DatePicker
											value={workOrder?.dueDate}
											onChange={(date) => {
												if (isCompleted) return;
												handleUpdateField('dueDate', date);
											}}
											placeholder="Set due date"
											size="sm"
											disabled={
												isCompleted ||
												['cancelled', 'on_hold'].includes(workOrder?.status)
											}
										/>
									</div>
								</div>
								<div className="text-left">
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider mb-1">
										Assigned To
									</div>
									<span className="text-xs text-gray-900">
										{form.assignedTo && form.assignedTo !== 'unassigned'
											? getUserFullName(
													team?.rows?.find((u) => u.id === form.assignedTo),
												)
											: 'Unassigned'}
									</span>
								</div>
								{workOrder?.status === 'completed' &&
									workOrder?.completedDate && (
										<div className="text-left">
											<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider mb-1">
												Completed
											</div>
											<span className="text-xs font-medium">
												{moment(workOrder.completedDate).format('MMM D, YYYY')}
											</span>
										</div>
									)}
								<div className="text-left">
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider mb-1">
										Source
									</div>
									{workOrder?.serviceScheduleId ? (
										<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
											<CalendarDaysIcon className="w-3 h-3" />
											PM Schedule
										</span>
									) : workOrder?.ticketId ? (
										<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
											<ClipboardDocumentListIcon className="w-3 h-3" />
											Ticket
										</span>
									) : (
										<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
											<UserIcon className="w-3 h-3" />
											Manual
										</span>
									)}
								</div>
							</div>

							{/* Status Quick Actions */}
							<div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
								Status
							</div>
							<div className="flex flex-wrap gap-2 mt-3">
								{STATUS_OPTIONS.map((statusOption) => (
									<button
										key={statusOption.key}
										onClick={() => {
											if (statusOption.key !== form.status) {
												handleStatusChange(statusOption.key);
											}
										}}
										className={classNames(
											'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
											form.status === statusOption.key
												? 'bg-indigo-600 text-white'
												: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
										)}
									>
										{statusOption.name}
									</button>
								))}
							</div>

							<div className="">
								<div className="mt-6 text-xs text-gray-500 font-medium uppercase tracking-wider">
									Description
								</div>

								{/* Description */}
								{workOrder?.description ? (
									<div className="whitespace-pre-wrap pt-3 text-sm">
										{workOrder.description}
									</div>
								) : (
									<div className="rounded-lg pt-3 text-sm">
										No description provided
									</div>
								)}
							</div>

							<div>
								<div className="mt-6 text-xs text-gray-500 font-medium uppercase tracking-wider">
									Assets
									{workOrder?.assets?.length > 1 && (
										<span className="text-gray-400 font-normal ml-1">
											({workOrder.assets.length})
										</span>
									)}
								</div>

								<div className="mt-3 space-y-2">
									{workOrder?.assets && workOrder.assets.length > 0 ? (
										workOrder.assets.map((asset) => {
											const hasMultipleAssets = workOrder.assets.length > 1;
											return (
												<AssetInfoCard
													key={asset.id}
													asset={asset}
													assetDisplayTemplate={assetDisplayTemplate}
													parseAssetTemplate={parseAssetTemplate}
													onViewAsset={() => {
														handleClose(`/assets/${asset.id}`);
														navigate(`/assets/${asset.id}/overview`);
													}}
													onContractClick={(contract) =>
														openContract({ id: contract.id })
													}
													onVendorClick={(contact) =>
														openViewContact({
															id: contact.id,
															onNavigate: () => setClose(),
														})
													}
													collapsed={hasMultipleAssets && assetsCollapsed}
													showCollapseToggle={hasMultipleAssets}
													onToggleCollapse={() =>
														setAssetsCollapsed(!assetsCollapsed)
													}
												/>
											);
										})
									) : (
										<div className="rounded-lg border px-5 py-3 text-sm text-gray-500">
											No assets attached
										</div>
									)}
								</div>
							</div>

							{/* Vendors/Contacts */}
							{(() => {
								// Get all contact IDs that are already shown as asset vendors
								const assetVendorIds = new Set(
									(workOrder?.assets || [])
										.flatMap((asset) => asset.contacts || [])
										.map((c) => c.id),
								);
								// Filter out contacts already shown in assets
								const filteredContacts = (workOrder?.contacts || []).filter(
									(c) => !assetVendorIds.has(c.id),
								);
								if (filteredContacts.length === 0) return null;
								return (
									<div>
										<div className="mt-6 text-xs text-gray-500 font-medium uppercase tracking-wider">
											Vendors / Service Providers
										</div>
										<div>
											<div className="mt-3 space-y-2">
												{filteredContacts.map((contact) => {
													const isCompany =
														contact.type === 'company' ||
														(contact.companyName && !contact.firstName);
													const displayName = isCompany
														? contact.companyName || contact.name
														: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
															contact.name;
													const secondaryText = isCompany
														? contact.email
														: contact.companyName || contact.email;

													return (
														<div
															key={contact.id}
															className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
															onClick={() =>
																openViewContact({
																	id: contact.id,
																	onNavigate: () => setClose(),
																})
															}
														>
															<div className="flex-1 min-w-0">
																<div className="text-sm font-medium text-gray-900 flex items-center gap-2">
																	{displayName}
																	{isCompany && (
																		<span className="text-xs text-blue-600 font-normal">
																			Company
																		</span>
																	)}
																</div>
																{secondaryText && (
																	<div className="text-xs text-gray-500 truncate">
																		{secondaryText}
																	</div>
																)}
															</div>
															<svg
																xmlns="http://www.w3.org/2000/svg"
																width="16"
																height="16"
																viewBox="0 0 24 24"
																fill="none"
																stroke="currentColor"
																strokeWidth="2"
																strokeLinecap="round"
																strokeLinejoin="round"
																className="text-gray-400 flex-shrink-0 ml-2"
															>
																<path d="m9 18 6-6-6-6" />
															</svg>
														</div>
													);
												})}
											</div>
										</div>
									</div>
								);
							})()}

							{/* Service Report Section */}
							{workOrder?.serviceReportRequired && (
								<div>
									<div className="mt-6 text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">
										Service Report
									</div>
									{workOrder?.serviceReport ? (
										<div className="flex items-center gap-3 p-3  border border-green-200 rounded-lg">
											<div
												className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
												onClick={() =>
													openDocumentPreview(workOrder.serviceReport)
												}
											>
												<div className="p-2 rounded-lg">
													<DocumentTextIcon className="w-5 h-5 text-green-600" />
												</div>
												<div className="flex-1 min-w-0">
													<p className="text-sm font-medium text-gray-900 truncate">
														{workOrder.serviceReport.name ||
															workOrder.serviceReport.originalName ||
															'Service Report'}
													</p>
													<p className="text-xs text-green-600">
														Uploaded{' '}
														{moment(workOrder.serviceReport.createdAt).format(
															'MMM D, YYYY',
														)}
													</p>
												</div>
											</div>
											<div className="flex items-center gap-2">
												<CheckIcon className="w-5 h-5 text-green-600" />
												{!isCompleted && (
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															onOpenDeleteModal({
																title: 'Remove service report?',
																description:
																	'Are you sure you want to remove the attached service report? The document will remain in your library.',
																confirmText: 'Remove',
																onConfirm: async () => {
																	await onUpdate({
																		id: open?.id,
																		serviceReportId: null,
																	});
																	refreshWorkOrder({ id: open?.id });
																	closeDeleteModal();
																},
															});
														}}
														className="p-1 rounded hover:bg-green-200 transition-colors"
													>
														<XMarkIcon className="w-4 h-4 text-green-700 hover:text-red-600" />
													</button>
												)}
											</div>
										</div>
									) : (
										<div
											className={classNames(
												'p-3 mt-3 border rounded-lg',
												isCompleted
													? 'bg-amber-50 border-amber-200'
													: 'bg-white border-gray-200',
											)}
										>
											<div className="flex items-center gap-2 mb-2">
												<ExclamationTriangleIcon
													className={classNames(
														'w-5 h-5',
														isCompleted ? 'text-amber-600' : 'text-gray-400',
													)}
												/>
												<span
													className={classNames(
														'text-sm font-medium',
														isCompleted ? 'text-amber-800' : 'text-gray-700',
													)}
												>
													Service report required
												</span>
											</div>

											<FileUpload
												files={[]}
												onChange={handleServiceReportChange}
												multiple={false}
												showLibraryOption
												acceptTypes="application/pdf,.doc,.docx"
												buttonText={
													isUploadingServiceReport
														? 'Uploading...'
														: 'Select Report'
												}
												disabled={isUploadingServiceReport}
											/>
										</div>
									)}
								</div>
							)}

							<div>
								<div className="flex items-center justify-between mt-6">
									<div className=" text-xs text-gray-500 font-medium uppercase tracking-wider">
										Attachments
									</div>
									<div className="relative">
										<button
											type="button"
											onClick={() => setShowUploadOptions(!showUploadOptions)}
											className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
										>
											<PlusIcon className="w-3.5 h-3.5" />
											{isUploading ? 'Uploading...' : 'Add Attachment'}
										</button>

										{/* Upload Options Dropdown */}
										{showUploadOptions && (
											<>
												<div
													className="fixed inset-0 z-40"
													onClick={() => setShowUploadOptions(false)}
												/>
												<div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
													<button
														type="button"
														onClick={() => {
															fileInputRef.current?.click();
															setShowUploadOptions(false);
														}}
														className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
													>
														<ArrowUpTrayIcon className="w-4 h-4 text-gray-500" />
														Upload File
													</button>
													<button
														type="button"
														onClick={() => {
															setShowUploadOptions(false);
															openDocumentPicker({
																multiSelect: true,
																onSelect: (selectedDocs) => {
																	console.log('selectedDocs: ', selectedDocs);
																	if (selectedDocs?.length > 0) {
																		setIsUploading(true);
																		onUploadDocuments(
																			{
																				id: open?.id,
																				documentIds: selectedDocs.map(
																					(doc) => doc.id,
																				),
																			},
																			() => {
																				setIsUploading(false);
																				refreshWorkOrder({ id: open?.id });
																			},
																		);
																	}
																},
															});
														}}
														className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
													>
														<FolderOpenIcon className="w-4 h-4 text-gray-500" />
														Choose from Library
													</button>
												</div>
											</>
										)}

										{/* Hidden file input */}
										<input
											ref={fileInputRef}
											type="file"
											accept="image/jpeg, image/png, application/pdf, .doc, .docx, .xls, .xlsx"
											onChange={handleFileUpload}
											className="hidden"
										/>
									</div>
								</div>

								{/* Non-image Attachments */}
								<AttachmentList
									attachments={getNonImageAttachments(workOrder?.documents)}
									showEmpty={false}
									onClick={(attachment) => openDocumentPreview(attachment)}
									onDelete={(attachment) => {
										onOpenDeleteModal({
											title: 'Remove attachment?',
											description: (
												<span>
													Are you sure you want to remove "{attachment.name}"?
													This action cannot be undone.
												</span>
											),
											confirmText: 'Remove',
											onConfirm: async () => {
												await onRemoveDocument({
													id: open?.id,
													documentId: attachment.id,
												});
												refreshWorkOrder({ id: open?.id });
												closeDeleteModal();
											},
										});
									}}
								/>

								{/* Image Gallery */}
								<ImageGallery
									className="mt-3"
									attachments={workOrder?.documents}
									onClick={(attachment) => openDocumentPreview(attachment)}
									onDelete={(attachment) => {
										onOpenDeleteModal({
											title: 'Remove image?',
											description: (
												<span>
													Are you sure you want to remove "{attachment.name}"?
													This action cannot be undone.
												</span>
											),
											confirmText: 'Remove',
											onConfirm: async () => {
												await onRemoveDocument({
													id: open?.id,
													documentId: attachment.id,
												});
												refreshWorkOrder({ id: open?.id });
												closeDeleteModal();
											},
										});
									}}
								/>

								{/* Empty state when no attachments at all */}
								{(!workOrder?.documents ||
									workOrder?.documents?.length === 0) && (
									<p className="text-sm text-gray-500 mt-3">No attachments</p>
								)}
							</div>

							{/* SOP Section - Show linked SOPs as clickable links */}
							{(() => {
								// Get unique SOPs from checklist items
								const sopItems = (checklistItems || []).filter(
									(item) => item.sopId || item.sop_id,
								);
								if (sopItems.length === 0) return null;

								// Group by SOP to get unique SOPs
								const uniqueSops = sopItems.reduce((acc, item) => {
									const sopId = item.sopId || item.sop_id;
									if (!acc[sopId]) {
										const v = item.sopVersion;
										const version =
											v?.versionMajor != null
												? `${v.versionMajor}.${v.versionMinor}`
												: null;
										acc[sopId] = {
											sop: item.sop,
											sopVersion: item.sopVersion,
											version,
											versionId: item.sopVersionId || item.sop_version_id,
											// Count completed steps for this SOP
											items: [],
										};
									}
									acc[sopId].items.push(item);
									return acc;
								}, {});

								return (
									<div className="mt-6">
										<div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
											Standard Operating Procedures
										</div>
										<div className="mt-3 space-y-2">
											{Object.values(uniqueSops).map((sopData) => {
												// Calculate completed steps from sopResponses
												const sopResponses = sopData.items.reduce(
													(acc, item) => {
														if (item.sopResponses) {
															Object.assign(
																acc.completedSteps,
																item.sopResponses.completedSteps || {},
															);
															Object.assign(
																acc.skippedSteps,
																item.sopResponses.skippedSteps || {},
															);
															Object.assign(
																acc.stepInputs,
																item.sopResponses.stepInputs || {},
															);
														}
														return acc;
													},
													{
														completedSteps: {},
														skippedSteps: {},
														stepInputs: {},
													},
												);

												const sopSteps =
													sopData.sopVersion?.steps || sopData.sop?.steps || [];
												const sopCompletedStepsCount =
													sopResponses.completedSteps
														? Object.values(sopResponses.completedSteps).filter(
																Boolean,
															).length
														: 0;

												return (
													<button
														key={sopData.sop?.id}
														type="button"
														className="w-full flex items-center justify-between border border-gray-300 rounded-md px-4 py-3 hover:bg-indigo-50 hover:border-indigo-100 transition-colors text-left group"
														onClick={() => {
															// Merge SOP and version data for the viewer
															// The viewer needs: name, description, steps, versionMajor, versionMinor
															const sopForViewer = {
																...sopData.sop,
																...sopData.sopVersion,
																name: sopData.sop?.name,
																// Use version steps if available, otherwise fall back to SOP steps
																steps: sopSteps,
															};

															openSopViewer({
																sop: sopForViewer,
																initialCompletedSteps:
																	sopResponses.completedSteps,
																initialSkippedSteps: sopResponses.skippedSteps,
																stepInputs: sopResponses.stepInputs,
																readOnly: isCompleted,
																onComplete: isCompleted
																	? null
																	: async (data) => {
																			// Save SOP responses to the first checklist item for this SOP
																			const firstItem = sopData.items[0];
																			if (firstItem) {
																				try {
																					await onUpdateChecklistItem({
																						id: open?.id,
																						checklistItemId: firstItem.id,
																						sopResponses: {
																							completedSteps:
																								data.completedSteps,
																							skippedSteps: data.skippedSteps,
																							stepInputs: data.stepInputs,
																						},
																					});
																					refreshChecklist({ id: open?.id });
																				} catch (err) {
																					console.error(
																						'Error saving SOP responses:',
																						err,
																					);
																				}
																			}
																		},
															});
														}}
													>
														<div>
															<span className="text-sm font-medium text-indigo-900">
																{sopData.sop?.name || 'SOP'}
															</span>
															{sopSteps.length > 0 && (
																<p className="text-xs text-gray-600 mt-0.5">
																	{`Steps: ${sopCompletedStepsCount} / ${sopSteps.length}`}
																</p>
															)}
														</div>
														<div className="flex items-center gap-2">
															<span className="text-xs text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
																Open SOP
															</span>
														</div>
													</button>
												);
											})}
										</div>
									</div>
								);
							})()}

							{/* Tasks Section */}
							<div className="mt-6">
								<div className=" text-xs text-gray-500 font-medium uppercase tracking-wider">
									Tasks
								</div>

								<TaskList
									items={(checklistItems || []).filter(
										(item) => !item.sopId && !item.sop_id,
									)}
									team={team?.rows}
									onToggle={handleToggleTask}
									onAssign={handleAssignTask}
									onDueDateChange={handleDueDateChange}
									onDelete={handleDeleteTask}
									onReorder={handleReorderTasks}
									onInputChange={handleInputChange}
									onTaskClick={(task) => {
										openTaskDetail({
											task,
											team: team?.rows,
											onUpdate: async (taskId, updates) => {
												await onUpdateChecklistItem({
													id: open?.id,
													checklistItemId: taskId,
													...updates,
												});
												refreshChecklist({ id: open?.id });
											},
											onToggle: async (taskId, currentCompleted) => {
												await onUpdateChecklistItem({
													id: open?.id,
													checklistItemId: taskId,
													completed: !currentCompleted,
												});
												refreshChecklist({ id: open?.id });
											},
											onDelete: async (taskId) => {
												await handleDeleteTask(taskId);
											},
										});
									}}
									onAdd={(title) => {
										onCreateChecklistItem(
											{ id: open?.id, title },
											(createdTask) => {
												refreshChecklist({ id: open?.id });
												// Open task detail modal so user can add more info
												if (createdTask) {
													openTaskDetail({
														task: createdTask,
														team: team?.rows,
														hideToggle: true, // Skip toggle when just created
														onUpdate: async (taskId, updates) => {
															await onUpdateChecklistItem({
																id: open?.id,
																checklistItemId: taskId,
																...updates,
															});
															refreshChecklist({ id: open?.id });
														},
														onToggle: async (taskId, currentCompleted) => {
															await onUpdateChecklistItem({
																id: open?.id,
																checklistItemId: taskId,
																completed: !currentCompleted,
															});
															refreshChecklist({ id: open?.id });
														},
														onDelete: async (taskId) => {
															await handleDeleteTask(taskId);
														},
													});
												}
											},
										);
									}}
									emptyText="No tasks yet. Add one above!"
								/>

								{activeTab === 'assets' && (
									<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
										<p className="text-sm text-yellow-800">
											⚠️ Asset list display removed - needs AssetSelectorBeta
											integration or inline display
										</p>
									</div>
								)}

								{activeTab === 'activity' && (
									<div className="space-y-4">
										{/* Comment Input */}
										<div className="flex gap-2">
											<textarea
												value={comment}
												onChange={(e) => setComment(e.target.value)}
												placeholder="Add a comment..."
												rows={2}
												className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
											/>
											<Button
												onClick={handleSubmitComment}
												disabled={
													!comment.trim() || commentStatus === 'pending'
												}
											>
												Post
											</Button>
										</div>

										{/* Activity Timeline */}
										<div className="space-y-4">
											{workOrder?.activities?.length > 0 ? (
												workOrder.activities.map((activity) => (
													<div key={activity.id} className="flex gap-3">
														<AvatarGenerated
															firstName={activity.addedBy?.firstName}
															lastName={activity.addedBy?.lastName}
															size="sm"
														/>
														<div className="flex-1">
															<div className="text-sm">
																{generateActivityLine(activity)}
															</div>
															<div className="text-xs text-gray-400 mt-1">
																{moment(activity.createdAt).format(
																	'MMM D, YYYY [at] h:mmA',
																)}
															</div>
														</div>
													</div>
												))
											) : (
												<p className="text-sm text-gray-500 text-center py-8">
													No activity yet.
												</p>
											)}
										</div>
									</div>
								)}
							</div>

							{/* Comments Section */}
							<div className="mt-6">
								<div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
									Comments
								</div>

								{/* Existing Comments */}
								{workOrder?.activities
									?.filter((activity) => activity.type === 'comment.created')
									?.map((activityItem) => (
										<li
											key={activityItem.id}
											className="relative flex gap-x-4 mt-3"
										>
											<div className="absolute left-0 top-0 flex w-6 justify-center h-6">
												<div className="w-px bg-gray-200" />
											</div>

											<AvatarGenerated
												id={getUserFullName(activityItem?.addedByUser)}
												size="xxs"
												className="relative mt-3 h-6 w-6 flex-none rounded-full bg-gray-50"
											/>
											<div className="flex-auto rounded-md px-4 py-2 ring-1 ring-inset ring-gray-200">
												<div className="flex justify-between gap-x-4">
													<div className="py-0.5 text-xs leading-5 text-gray-500">
														<span className="font-medium text-gray-900">
															{getUserFullName(activityItem?.addedByUser) ||
																'Unknown'}
														</span>{' '}
														commented
													</div>
													<time
														dateTime={activityItem?.createdAt}
														className="flex-none py-0.5 text-xs leading-5 text-gray-500"
													>
														{moment(activityItem?.createdAt).fromNow()}
													</time>
												</div>
												<p className="text-xs text-gray-500 mt-1">
													{activityItem.data}
												</p>
											</div>
										</li>
									))}

								{/* Add Comment Input */}
								<div className="mt-3">
									<div className="flex space-x-3">
										<div className="flex-shrink-0 -ml-1">
											<AvatarGenerated id={getUserFullName(user)} size="xs" />
										</div>
										<div className="min-w-0 flex-1">
											<textarea
												value={comment}
												onChange={(e) => setComment(e.target.value)}
												placeholder="Leave a comment..."
												rows={3}
												className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none"
											/>
											<div className="mt-3 flex items-center justify-end">
												<Button
													onClick={handleSubmitComment}
													disabled={
														!comment.trim() || commentStatus === 'pending'
													}
												>
													{commentStatus === 'pending'
														? 'Adding...'
														: 'Add Comment'}
												</Button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Column 2 - Service Report, SOP, Tasks */}
						<div className="lg:col-span-4">
							<div className="hidden lg:block space-y-4 mt-6">
								{/* Assigned To */}
								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Assigned To
									</div>
									<div className="mt-1">
										<Listbox
											value={form.assignedTo || ''}
											disabled={isCompleted}
											onChange={(val) => {
												if (isCompleted) return;
												handleUpdateField('assignedTo', val || null);
											}}
										>
											<div className="relative">
												<Listbox.Button
													className="flex items-center gap-1"
													disabled={isCompleted}
												>
													<span
														className={classNames(
															'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
															isCompleted ? 'cursor-default' : 'cursor-pointer',
															'bg-gray-100 text-gray-700',
															!isCompleted && 'hover:bg-gray-200',
														)}
													>
														{workOrder?.assignedToUser ? (
															<span className="flex items-center gap-1.5">
																<AvatarGenerated
																	id={getUserFullName(
																		workOrder?.assignedToUser,
																	)}
																	size="xxs"
																/>
																{getUserFullName(workOrder?.assignedToUser)}
															</span>
														) : (
															'Unassigned'
														)}
													</span>
												</Listbox.Button>
												<Listbox.Options className="absolute left-0 z-10 mt-1 max-h-60 w-56 overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
													<Listbox.Option
														value=""
														className={({ active, selected }) =>
															classNames(
																'cursor-pointer select-none relative py-2 pl-3 pr-9',
																active ? 'bg-indigo-50' : '',
																selected ? 'bg-indigo-100' : '',
															)
														}
													>
														{({ selected }) => (
															<div className="flex items-center">
																<span
																	className={classNames(
																		'text-gray-500 text-xs',
																		selected && 'font-semibold',
																	)}
																>
																	Unassigned
																</span>
																{selected && (
																	<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																)}
															</div>
														)}
													</Listbox.Option>
													{team?.rows
														?.filter(
															(member) => member.active && !member.archived,
														)
														?.map((member) => (
															<Listbox.Option
																key={member.id}
																value={member.id}
																className={({ active, selected }) =>
																	classNames(
																		'cursor-pointer select-none relative py-2 pl-3 pr-9',
																		active ? 'bg-indigo-50' : '',
																		selected ? 'bg-indigo-100' : '',
																	)
																}
															>
																{({ selected }) => (
																	<div className="flex items-center text-xs">
																		<AvatarGenerated
																			id={getUserFullName(member)}
																			size="xxs"
																			className="mr-2"
																		/>
																		<span
																			className={
																				selected ? 'font-semibold' : ''
																			}
																		>
																			{member?.first_name || member?.firstName}{' '}
																			{member?.last_name || member?.lastName}
																		</span>
																		{selected && (
																			<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																		)}
																	</div>
																)}
															</Listbox.Option>
														))}
												</Listbox.Options>
											</div>
										</Listbox>
									</div>
								</div>

								{/* Category */}
								<div className="text-left">
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider mb-1">
										Category
									</div>
									<Listbox
										value={workOrder?.category?.id || ''}
										disabled={isCompleted}
										onChange={(val) => {
											if (isCompleted) return;
											onUpdate({ id: open?.id, categoryId: val || null }, () =>
												refreshWorkOrder({ id: open?.id }, null, {
													silent: true,
												}),
											);
										}}
									>
										<div className="relative">
											<Listbox.Button
												className="flex items-center gap-1"
												disabled={isCompleted}
											>
												<span
													className={classNames(
														'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
														isCompleted ? 'cursor-default' : 'cursor-pointer',
														'bg-gray-100 text-gray-700',
														!isCompleted && 'hover:bg-gray-200',
													)}
												>
													{workOrder?.category?.name || 'No category'}
												</span>
											</Listbox.Button>
											<Listbox.Options className="absolute left-0 z-10 mt-1 max-h-60 w-48 overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
												<Listbox.Option
													value=""
													className={({ active, selected }) =>
														classNames(
															'cursor-pointer select-none relative py-2 pl-3 pr-9',
															active ? 'bg-indigo-50' : '',
															selected ? 'bg-indigo-100' : '',
														)
													}
												>
													{({ selected }) => (
														<div className="flex items-center">
															<span
																className={classNames(
																	'text-gray-500 text-xs',
																	selected && 'font-semibold',
																)}
															>
																No category
															</span>
															{selected && (
																<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
															)}
														</div>
													)}
												</Listbox.Option>
												{categories
													?.sort((a, b) => a.name.localeCompare(b.name))
													?.map((category) => (
														<Listbox.Option
															key={category.id}
															value={category.id}
															className={({ active, selected }) =>
																classNames(
																	'cursor-pointer select-none relative py-2 pl-3 pr-9',
																	active ? 'bg-indigo-50' : '',
																	selected ? 'bg-indigo-100' : '',
																)
															}
														>
															{({ selected }) => (
																<div className="flex items-center text-xs">
																	<span
																		className={selected ? 'font-semibold' : ''}
																	>
																		{category.name}
																	</span>
																	{selected && (
																		<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																	)}
																</div>
															)}
														</Listbox.Option>
													))}
												<div className="border-t border-gray-100 mt-1 pt-1">
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															openAddCategory({
																moduleId: workOrdersModule?.id,
																onSubmit: async (category) => {
																	console.log('Category created:', category);
																	if (!category?.value) {
																		console.error('Category ID is missing');
																		return;
																	}

																	try {
																		await onUpdate({
																			id: open?.id,
																			categoryId: category.value,
																		});
																		console.log(
																			'Work order updated successfully',
																		);

																		// Refresh work order to get updated data with category
																		await refreshWorkOrder({ id: open?.id });
																		console.log('Work order refreshed');

																		// Refresh categories list
																		if (workOrdersModule?.id) {
																			await onRefreshCategories({
																				id: workOrdersModule.id,
																			});
																			console.log('Categories refreshed');
																		}
																	} catch (error) {
																		console.error(
																			'Error updating work order:',
																			error,
																		);
																	}
																},
															});
														}}
														className="w-full text-left px-3 py-2 text-xs text-indigo-600 hover:bg-indigo-50 flex items-center"
													>
														<PlusIcon className="h-4 w-4 mr-1.5" />
														Add category
													</button>
												</div>
											</Listbox.Options>
										</div>
									</Listbox>
								</div>

								{/* Source */}
								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Source
									</div>
									<div className="mt-1">
										{workOrder?.serviceScheduleId ? (
											<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium">
												<CalendarDaysIcon className="w-3 h-3" />
												PM Schedule
											</span>
										) : workOrder?.ticketId ? (
											<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ">
												<ClipboardDocumentListIcon className="w-3 h-3" />
												Ticket
											</span>
										) : (
											<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium">
												<UserIcon className="w-3 h-3" />
												Manual
											</span>
										)}
									</div>
								</div>

								{/* Created */}
								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Created
									</div>
									<div className="text-xs mt-1">
										{moment(workOrder?.createdAt).format(
											'MMM D, YYYY [at] h:mmA',
										)}
									</div>
								</div>

								{/* Last Updated */}
								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Last Updated
									</div>
									<div className="text-xs mt-1">
										{moment(workOrder?.updatedAt).format(
											'MMM D, YYYY [at] h:mmA',
										)}
									</div>
								</div>

								{/* Linked Ticket */}
								{workOrder?.ticket && (
									<div>
										<div className="mt-6 text-xs text-gray-500 font-medium uppercase tracking-wider">
											Linked Ticket
										</div>
										<div
											className="mt-3 flex items-center justify-between rounded-md border p-3 cursor-pointer hover:bg-gray-50"
											onClick={() => {
												setClose();
												openTicketOverview({
													id: workOrder.ticket.id,
												});
											}}
										>
											<div className="flex items-center gap-2">
												<div>
													<TextStack
														primary={workOrder.ticket.subject}
														secondary={`#${workOrder.ticket.ticketCode}`}
													/>
												</div>
											</div>
											<StatusBadge status={workOrder.ticket.status} />
										</div>
									</div>
								)}

								{/* Contract */}
								{workOrder?.contract && (
									<div>
										<div className="mt-6 text-xs text-gray-500 font-medium uppercase tracking-wider">
											Contract
										</div>
										<div
											className="mt-3 rounded-lg border px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
											onClick={() =>
												openContract({ id: workOrder.contract.id })
											}
										>
											<div className="flex items-center justify-between">
												<TextStack
													primary={
														workOrder.contract.category?.name
															? `${workOrder.contract.name} (${workOrder.contract.category.name})`
															: workOrder.contract.name
													}
													secondary={
														workOrder.contract.endDate && (
															<span
																className={classNames(
																	'text-xs',
																	moment(workOrder.contract.endDate).isBefore(
																		moment(),
																	)
																		? 'text-red-600 font-medium'
																		: 'text-gray-500',
																)}
															>
																{moment(workOrder.contract.endDate).isBefore(
																	moment(),
																)
																	? 'Expired'
																	: `Expires ${moment(workOrder.contract.endDate).format('MMM D, YYYY')}`}
															</span>
														)
													}
													size="sm"
												/>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="16"
													height="16"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
													className="text-gray-400 flex-shrink-0 ml-2"
												>
													<path d="m9 18 6-6-6-6" />
												</svg>
											</div>
										</div>
									</div>
								)}

								{/* Edit/Statistics Button */}
								{isCompleted ? (
									<Button
										onClick={() =>
											openStatistics({
												id: workOrder?.id,
												entityType: 'workorder',
											})
										}
										type="primary"
										Icon={ChartBarIcon}
										className="mt-4 w-full justify-center"
									>
										View Statistics
									</Button>
								) : (
									<Button
										onClick={handleEdit}
										type="primary"
										Icon={PencilIcon}
										className="mt-4 w-full justify-center"
									>
										Edit Work Order
									</Button>
								)}

								{/* Audit */}
								<Button
									type="ghost"
									className=" w-full justify-center -mt-2"
									onClick={() => {
										handleClose();
										navigate(
											`/audit-log?entityType=work-order&entityId=${workOrder?.id}`,
										);
									}}
								>
									View Audit Logs
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Ticket Resolution Confirmation Modal */}
				{showTicketResolveModal && (
					<StackableModal
						open={showTicketResolveModal}
						onClose={() => setShowTicketResolveModal(false)}
						size="lg"
						title="Complete Work Order"
						dialogClassName="px-2"
					>
						<div className="space-y-4">
							<p className="text-sm text-gray-700">
								This work order is linked to ticket #
								{workOrder?.ticket?.ticketCode}.
							</p>
							<p className="text-sm text-gray-600">
								Would you like to resolve the ticket as well?
							</p>

							<div className="flex justify-between gap-1 pt-6 ">
								<button
									type="button"
									onClick={() => handleCompleteWithTicket(false)}
									className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
								>
									Complete Work Order Only
								</button>
								<button
									type="button"
									onClick={() => handleCompleteWithTicket(true)}
									className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
								>
									Complete & Resolve Ticket
								</button>
							</div>
						</div>
					</StackableModal>
				)}
			</StackableModal>
		</>
	);
};

export default WorkOrderOverview;
